import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Text,
  useToast,
  Spinner,
  Badge,
  Link,
  Select,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  CheckIcon,
  CloseIcon,
} from "@chakra-ui/icons";
import { supabase } from "../../lib/supabaseClient";

const PAGE_SIZE = 10;

const AdminVideoApplications = () => {
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result, error } = await supabase.rpc(
        "get_channel_video_info",
        {
          keyword: searchQuery || null,
          page: page,
        }
      );

      if (error) {
        throw error;
      }

      if (result && result.length > 0) {
        // 상태 필터링 적용
        let filteredData = result;
        if (statusFilter !== null) {
          filteredData = result.filter((item) => {
            if (statusFilter === true) {
              return item.status === true;
            } else if (statusFilter === false) {
              return item.status === false || item.status === null;
            }
            return true;
          });
        }

        setData(filteredData);
        // 첫 번째 행에서 total_count를 가져옴
        setTotalCount(result[0].total_count || 0);
        setTotalPages(Math.ceil((result[0].total_count || 0) / PAGE_SIZE));
      } else {
        setData([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("데이터 조회 실패:", error);
      toast({
        title: "데이터 조회 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setData([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, statusFilter, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchTrigger = () => {
    setPage(1);
    setSearchQuery(searchTerm);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchTrigger();
    }
  };

  const handleStatusFilterChange = (value) => {
    const boolValue =
      value === "true" ? true : value === "false" ? false : null;
    setStatusFilter(boolValue);
    setPage(1);
  };

  const handleToggleStatus = async (item) => {
    const newStatus = !item.status;
    try {
      const { error } = await supabase
        .from("channel_to_video")
        .update({ status: newStatus })
        .eq("id", item.id);
      if (error) {
        throw error;
      }
      toast({
        title: newStatus ? "승인 완료" : "승인 취소",
        description: newStatus
          ? "영상 신청이 승인되었습니다."
          : "영상 신청 승인이 취소되었습니다.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchData();
    } catch (error) {
      console.error("상태 변경 실패:", error);
      toast({
        title: "상태 변경 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 거절/복구 토글 함수
  const handleToggleRejected = async (item) => {
    const newRejected = !item.rejected_bool;
    try {
      const { error } = await supabase
        .from("channel_to_video")
        .update({ rejected_bool: newRejected })
        .eq("id", item.id);
      if (error) throw error;
      toast({
        title: newRejected ? "거절 처리됨" : "복구 처리됨",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchData();
    } catch (error) {
      toast({
        title: "처리 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const formatChannelType = (type) => {
    const typeMap = {
      youtube: "유튜브",
      tiktok: "틱톡",
      instagram: "인스타그램",
      other: "기타",
    };
    return typeMap[type] || type;
  };

  const getChannelTypeColor = (type) => {
    const colorMap = {
      youtube: "red",
      tiktok: "black",
      instagram: "pink",
      other: "gray",
    };
    return colorMap[type] || "gray";
  };

  const getStatusBadge = (status) => {
    if (status === true) {
      return <Badge colorScheme="green">승인됨</Badge>;
    } else {
      return <Badge colorScheme="gray">대기중</Badge>;
    }
  };

  const columns = [
    { key: "channel_title", label: "채널명" },
    { key: "channel_type", label: "채널 타입" },
    { key: "company_list", label: "회사" },
    { key: "channel_url", label: "채널 URL" },
    { key: "channel_user", label: "채널 사용자" },
    { key: "title", label: "영상 제목" },
    { key: "identification_code", label: "식별코드" },
    { key: "licensed_company", label: "라이센스 회사" },
  ];

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h1" size="lg">
          영상 신청 관리
        </Heading>
      </Flex>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        {/* 검색 */}
        <Flex mb={4} gap={2} align="center">
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="채널명, 영상 제목, 사용자로 검색"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </InputGroup>
          <Button onClick={handleSearchTrigger}>검색</Button>
          <Select
            maxW="150px"
            value={
              statusFilter === true
                ? "true"
                : statusFilter === false
                ? "false"
                : ""
            }
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            placeholder="상태 선택"
          >
            <option value="">모든 상태</option>
            <option value="true">승인됨</option>
            <option value="false">대기중</option>
          </Select>
        </Flex>

        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner />
          </Flex>
        ) : (
          <>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  {columns.map((column) => (
                    <Th key={column.key}>{column.label}</Th>
                  ))}
                  <Th>작업</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.map((item, index) => (
                  <Tr key={item.id || index}>
                    <Td
                      bg="blue.50"
                      borderLeft="4px solid"
                      borderColor="blue.400"
                    >
                      <Text
                        fontWeight="bold"
                        fontSize="md"
                        color="blue.800"
                        noOfLines={2}
                      >
                        {item.channel_title || "-"}
                      </Text>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={getChannelTypeColor(item.channel_type)}
                      >
                        {formatChannelType(item.channel_type)}
                      </Badge>
                    </Td>
                    <Td>
                      <Text noOfLines={1}>{item.company_list || "-"}</Text>
                    </Td>
                    <Td>
                      {item.channel_url ? (
                        <Link
                          href={item.channel_url}
                          isExternal
                          color="blue.500"
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Text noOfLines={1}>링크</Text>
                          <ExternalLinkIcon />
                        </Link>
                      ) : (
                        "-"
                      )}
                    </Td>
                    <Td>
                      <Text noOfLines={1}>{item.channel_user || "-"}</Text>
                    </Td>
                    <Td
                      bg="green.50"
                      borderLeft="4px solid"
                      borderColor="green.400"
                    >
                      <Text
                        fontWeight="bold"
                        fontSize="md"
                        color="green.800"
                        noOfLines={2}
                      >
                        {item.title || "-"}
                      </Text>
                    </Td>
                    <Td>
                      <Text
                        fontFamily="mono"
                        fontSize="sm"
                        color="blue.600"
                        fontWeight="bold"
                      >
                        {item.identification_code || "-"}
                      </Text>
                    </Td>
                    <Td>
                      <Text noOfLines={1}>{item.licensed_company || "-"}</Text>
                    </Td>
                    <Td>
                      <Button
                        size="xs"
                        colorScheme={item.status ? "green" : "blue"}
                        onClick={() => handleToggleStatus(item)}
                        mr={2}
                      >
                        {item.status ? "승인" : "대기중"}
                      </Button>
                      {item.rejected_bool ? (
                        <Button
                          size="xs"
                          colorScheme="gray"
                          onClick={() => handleToggleRejected(item)}
                        >
                          복구
                        </Button>
                      ) : (
                        <Button
                          size="xs"
                          colorScheme="red"
                          onClick={() => handleToggleRejected(item)}
                        >
                          거절
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            <Flex justify="space-between" align="center" mt={6}>
              <Text fontSize="sm" color="gray.600">
                총 {totalCount}개 신청
              </Text>
              <Flex align="center">
                <IconButton
                  icon={<ChevronLeftIcon />}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  isDisabled={page === 1}
                  size="sm"
                />
                <Text mx={4} fontSize="sm">
                  {page} / {totalPages}
                </Text>
                <IconButton
                  icon={<ChevronRightIcon />}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  isDisabled={page === totalPages}
                  size="sm"
                />
              </Flex>
            </Flex>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AdminVideoApplications;
